import { useMutation, useQueryClient } from 'react-query'
import callAPI from '../../../API/callSecureAPI'
import endpoints from '../../../API/endpoints'
import { useAuth0 } from '../../../contexts/auth0-context'
import { useStrategy } from '../../hooks/useStrategy'
import { LinkingItemType } from '../../../components/Linking/Linking.types'
import useInvalidateLinkingQueries from '../linking/useInvalidateLinkingQueries'
import { getStrategicImperativesQueryKey, StrategicImperative } from './useStrategicImperativesQuery'

const AUDIENCE = { audience: 'https://atlas.aesara.com' }

type Endpoint = { url: string; method: string }

const useStrategicImperativeRequest = () => {
    const { getTokenSilently } = useAuth0()

    return async (
        endpoint: Endpoint,
        failureMessage: string,
        { path = '', body }: { path?: string; body?: unknown } = {}
    ) => {
        const token = await getTokenSilently(AUDIENCE)
        const response = await callAPI(endpoint.url + path, endpoint.method, token, body)

        if (!response.success) {
            throw new Error(response.error ?? failureMessage)
        }

        return response
    }
}

const useRefreshStrategicImperatives = () => {
    const { invalidateLinkingQueries, invalidateQueriesShowingTypes } = useInvalidateLinkingQueries()

    return (alsoChangedTypes: LinkingItemType[] = []) =>
        Promise.all([
            invalidateQueriesShowingTypes(['strategicImperative', ...alsoChangedTypes]),
            invalidateLinkingQueries(),
        ])
}

type StrategicImperativeFields = Pick<StrategicImperative, 'name' | 'audiences'>

export const useCreateStrategicImperativeMutation = () => {
    const request = useStrategicImperativeRequest()
    const refreshStrategicImperatives = useRefreshStrategicImperatives()
    const strategyId = useStrategy()?._id

    return useMutation(
        async (fields: StrategicImperativeFields): Promise<StrategicImperative> =>
            (
                await request(endpoints.createStrategicImperative, 'Failed to create the strategic imperative', {
                    body: { ...fields, strategyId },
                })
            ).data,
        { onSuccess: () => refreshStrategicImperatives() }
    )
}

type UpdateStrategicImperativeVariables = {
    strategicImperativeId: string
    updates: StrategicImperativeFields
}

export const useUpdateStrategicImperativeMutation = () => {
    const request = useStrategicImperativeRequest()
    const refreshStrategicImperatives = useRefreshStrategicImperatives()

    return useMutation(
        async ({ strategicImperativeId, updates }: UpdateStrategicImperativeVariables): Promise<StrategicImperative> =>
            (
                await request(endpoints.updateStrategicImperative, 'Failed to update the strategic imperative', {
                    path: strategicImperativeId,
                    body: updates,
                })
            ).data,
        { onSuccess: () => refreshStrategicImperatives() }
    )
}

export const useDeleteStrategicImperativeMutation = () => {
    const request = useStrategicImperativeRequest()
    const refreshStrategicImperatives = useRefreshStrategicImperatives()

    return useMutation(
        async (strategicImperativeId: string): Promise<StrategicImperative> =>
            (
                await request(endpoints.deleteStrategicImperative, 'Failed to delete the strategic imperative', {
                    path: strategicImperativeId,
                })
            ).data,
        // The delete also unlinks it from its activities and value messages, whose lists count those links.
        { onSuccess: () => refreshStrategicImperatives(['activity', 'valueMessage']) }
    )
}

export const useReorderStrategicImperativesMutation = () => {
    const request = useStrategicImperativeRequest()
    const queryClient = useQueryClient()
    const strategyId = useStrategy()?._id
    const queryKey = getStrategicImperativesQueryKey(strategyId)

    return useMutation(
        (strategicImperatives: StrategicImperative[]) =>
            request(endpoints.reorderStrategicImperatives, 'Failed to save the new order', {
                body: strategicImperatives.map(({ _id }) => _id),
            }),
        {
            onMutate: async (strategicImperatives) => {
                await queryClient.cancelQueries(queryKey)

                const previous = queryClient.getQueryData<StrategicImperative[]>(queryKey)

                queryClient.setQueryData(
                    queryKey,
                    strategicImperatives.map((strategicImperative, index) => ({ ...strategicImperative, order: index }))
                )

                return { previous }
            },
            onError: (_error, _variables, context) => {
                const previous = (context as { previous?: StrategicImperative[] } | undefined)?.previous

                if (previous) {
                    queryClient.setQueryData(queryKey, previous)
                }
            },
            onSettled: () => queryClient.invalidateQueries(queryKey),
        }
    )
}
